<template>
  <article v-editable="blok" class="article-wrapper">
    <component :is="pageBannerComponent" v-if="pageBanner" :blok="pageBanner">
      <template #filters>
        <QuickAccessFilterBar />
      </template>
    </component>
    <PaginationError v-if="!loading && error" />
    <PaginationNoResults v-else-if="!loading && !results" />
    <div class="container">
      <QuickAccessResult
        v-if="mergedArticles.length"
        :expected-results="4"
        :filtered-audiences="filteredAudiences"
        :filtered-topics="filteredTopics"
        :show-more="false"
        :stories="mergedArticles"
        content-type="featured-topics"
        subheading="Relevant articles and topics that might help"
        title="Featured on Compass"
      />
      <QuickAccessResult
        v-if="resourceResults.length"
        :expected-results="4"
        :filtered-audiences="filteredAudiences"
        :filtered-topics="filteredTopics"
        :stories="resourceResults"
        content-type="resources"
        subheading="Collated content of interest"
        title="Resources"
      />
      <QuickAccessResult
        v-if="newsResults.length"
        :expected-results="5"
        :filtered-audiences="filteredAudiences"
        :filtered-topics="filteredTopics"
        :stories="newsResults"
        content-type="news"
        subheading="The most recent developments that match your request"
        title="News"
      />
      <QuickAccessResult
        v-if="eventsResults.length"
        :expected-results="3"
        :filtered-audiences="filteredAudiences"
        :filtered-topics="filteredTopics"
        :stories="eventsResults"
        content-type="events"
        subheading="Coming events – national, worldwide, or local to you"
        title="Events"
      />
    </div>
    <component
      :is="block.component"
      v-for="block in blocks"
      :key="block._uid"
      :blok="block"
    />
    <ContentSubscribe v-if="!pageContainsSubscriptionForm" />
  </article>
</template>

<script setup lang="ts">
import { QueryFormatterFactory } from '~/modules/queryFormatters/QueryFormatterFactory'
import type { QuickAccessResultsPageStoryblok } from '~/types/storyblok'

const props = defineProps<{ blok: QuickAccessResultsPageStoryblok }>()
const {
  pageBanner,
  pageBannerComponent,
  blocks,
  pageContainsSubscriptionForm,
} = useStoryContent(toRefs(props))

const componentType = ref('QuickAccess')
const { filterQuery, isFiltered, sortByQuery } = useFilters({ componentType })
const { fetchAsyncData } = useStoryblokHelpers()
/**
 * Fetch story results by folder path
 * @param {String} slugs Storyblok slugs ("folders" by wildcard)
 * @param {Number} quantity The maximum number of stories to fetch
 * @param {Boolean} showStartPages Include start (folder index) pages?
 * @param {Object} filters Filter Query to apply
 *
 * @returns {Object} Fetched stories
 */
const fetchStories = (slugs, filters, quantity = 6, showStartPages = false) =>
  fetchAsyncData('stories', {
    ...filters,
    ...sortByQuery.value,
    by_slugs: slugs,
    per_page: quantity,
    is_startpage: showStartPages,
    excluding_slugs: 'index,globals/*,search/*,quick-find/',
  })

const startOfToday = computed(() => {
  const date = new Date()
  date.setHours(0, 0, 0, 0)
  return date
})
/**
 * Fetch Events (from today forward)
 * @param {String} slugs Storyblok slugs ("folders" by wildcard)
 * @param {Number} quantity The maximum number of stories to fetch
 * @param {Object} filters Filter Query to apply
 *
 * @returns {Object} Fetched Events
 */
const fetchEvents = (slugs, filters, quantity = 3) =>
  fetchAsyncData('stories', {
    filter_query: {
      ...filters.filter_query,
      date_end: { gt_date: storyblokDateFormat(startOfToday.value) },
    },
    sort_by: 'content.date_start:asc',
    by_slugs: slugs,
    per_page: quantity,
  })

const articleSlugs =
  'featured-topics/*,elder-abuse/*,featured-articles/*,audiences/*'
const route = useRoute()
const queryFormatter = computed(() =>
  QueryFormatterFactory(componentType.value),
)
const filterQueryWithoutAllAudiences = computed(() =>
  queryFormatter.value.getFilterQuery({
    ...route.query,
    ignoreAllOption: true,
  }),
)
const { data, error, refresh, status } = await useAsyncData(
  () =>
    Promise.all([
      fetchStories(articleSlugs, filterQuery.value, 1, true),
      fetchStories(articleSlugs, filterQuery.value, 3),
      fetchStories('resources/*', filterQueryWithoutAllAudiences.value, 4),
      fetchStories('news/*', filterQueryWithoutAllAudiences.value, 5),
      fetchEvents('events/*', filterQueryWithoutAllAudiences.value),
    ]),
  {
    watch: [() => route.query],
    transform: ([
      { data: featuredData },
      { data: articleData },
      { data: resourceData },
      { data: newsData },
      { data: eventsData },
    ]) => {
      return [
        featuredData.value.data.stories,
        articleData.value.data.stories,
        resourceData.value.data.stories,
        newsData.value.data.stories,
        eventsData.value.data.stories,
      ]
    },
    getCachedData: getCachedDataDefault,
  },
)
onBeforeMount(() => {
  if (isFiltered.value) refresh()
})
const featuredResult = computed(() => data.value[0])
const articleResults = computed(() => data.value[1])
const resourceResults = computed(() => data.value[2])
const newsResults = computed(() => data.value[3])
const eventsResults = computed(() => data.value[4])
const loading = computed(() => status.value === 'pending')
const results = computed(
  () =>
    !!(
      featuredResult.value.length ||
      articleResults.value.length ||
      resourceResults.value.length ||
      newsResults.value.length ||
      eventsResults.value.length
    ),
)
const mergedArticles = computed(() =>
  featuredResult.value.concat(articleResults.value),
)
const filteredTopics = computed(() => [
  filterQuery.value.filter_query?.topics?.any_in_array,
])
const filteredAudiences = computed(() => [
  filterQuery.value?.filter_query?.audiences?.any_in_array,
])
</script>

<style lang="less" scoped>
// extras
</style>
